/**
 * Admin → Programs (Flagship system, iter 83)
 *
 * New compliance-aware program manager. Lists every flagship program and
 * routes the user into the detail editor (`AdminProgramDetail.jsx`) on
 * click. The detail editor exposes the full schema:
 *   * Program meta + 3 facing descriptions
 *   * Per-tier patient pricing + included service ids + refund/cancel
 *   * Per-service vendor cost, payment route (6 options), Nodera fee
 *     (flat or %), compliance flag, state/geo + required-docs
 *   * Live margin breakdown card per tier (calls the new
 *     /admin/flagship-programs/{id}/tiers/{tid}/breakdown endpoint)
 *
 * The legacy AdminPrograms.jsx (now `.legacy.jsx`) lives in the same
 * folder for reference; it'll be removed once iter 87 finishes wiring
 * the clinic-portal Activate flow to flagship_programs.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, ArrowRight, CheckCircle2, FlaskConical, Layers, Lock, Plus, ShieldCheck, Unlock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const COMPLIANCE_PILL = {
  low: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  counsel_required: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  restricted: "bg-rose-500/10 text-rose-300 border-rose-500/30",
  disabled: "bg-slate-500/10 text-slate-400 border-slate-500/30",
};

const COMPLIANCE_LABEL = {
  low: "Low review",
  counsel_required: "Counsel required",
  restricted: "Restricted",
  disabled: "Disabled",
};

function CompliancePill({ flag }) {
  const cls = COMPLIANCE_PILL[flag] || COMPLIANCE_PILL.counsel_required;
  return (
    <span
      data-testid={`flagship-compliance-${flag}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}
    >
      <ShieldCheck className="w-2.5 h-2.5" />
      {COMPLIANCE_LABEL[flag] || flag}
    </span>
  );
}

function ProgramCard({ p, readiness }) {
  const lowestTier = (p.tiers || []).reduce(
    (acc, t) => (acc === null || t.patient_price_cents < acc ? t.patient_price_cents : acc),
    null
  );
  // Three publish states:
  //   * active+ready → green Active pill
  //   * blocked (readiness loaded + ready=false) → red Locked pill
  //   * draft (is_active=false and not blocked, or readiness still loading) → slate Draft pill
  const blocked = readiness && readiness.ready === false;
  const issueCount = blocked
    ? (readiness.tiers || []).reduce((n, t) => n + (t.issues?.length || 0), 0)
    : 0;
  // First blocking issue, summarised in one line for the card.
  let firstIssueLine = null;
  if (blocked) {
    const firstTier = (readiness.tiers || []).find((t) => (t.issues || []).length > 0);
    const firstIssue = firstTier?.issues?.[0];
    if (firstIssue) {
      const tierName = firstTier.tier_name || "Tier";
      const svcName = firstIssue.service_name || "Service";
      if (firstIssue.kind === "doc_gap") {
        const missing = (firstIssue.missing || []).slice(0, 2).join("; ");
        firstIssueLine = `${tierName} · ${svcName} — missing: ${missing}`;
      } else if (firstIssue.kind === "state_gap") {
        const missing = (firstIssue.missing_states || []).slice(0, 3).join(", ");
        firstIssueLine = `${tierName} · ${svcName} — not credentialed in: ${missing}`;
      } else if (firstIssue.kind === "below_profit_floor") {
        firstIssueLine = `${tierName} — below profit floor (${(firstIssue.floor_percent || 0).toFixed(0)}%)`;
      } else if (typeof readiness.blocked_reason === "string") {
        firstIssueLine = readiness.blocked_reason;
      }
    } else if (typeof readiness.blocked_reason === "string") {
      firstIssueLine = readiness.blocked_reason;
    }
  }
  let pillCls, pillIcon, pillText, pillTestId;
  if (blocked) {
    pillCls = "bg-rose-500/15 border-rose-500/40 text-rose-200";
    pillIcon = <Lock className="w-2.5 h-2.5" />;
    pillText = issueCount > 0 ? `Locked · ${issueCount} issue${issueCount === 1 ? "" : "s"}` : "Locked";
    pillTestId = `flagship-card-locked-${p.slug}`;
  } else if (p.is_active) {
    pillCls = "bg-emerald-500/15 border-emerald-500/40 text-emerald-200";
    pillIcon = <Unlock className="w-2.5 h-2.5" />;
    pillText = "Active";
    pillTestId = `flagship-card-active-${p.slug}`;
  } else {
    pillCls = "bg-slate-700/40 border-slate-600/40 text-slate-300";
    pillIcon = <Lock className="w-2.5 h-2.5" />;
    pillText = "Draft";
    pillTestId = `flagship-card-draft-${p.slug}`;
  }

  return (
    <Link
      to={`/admin/programs/${p.id}`}
      data-testid={`flagship-program-card-${p.slug}`}
      className={`group block rounded-2xl border p-5 transition hover:bg-slate-900/60 ${
        blocked
          ? "border-rose-500/30 hover:border-rose-500/50 bg-slate-900/40"
          : "border-slate-800 hover:border-blue-500/40 bg-slate-900/40"
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
            {p.slug}
          </div>
          <div className="mt-1 text-lg font-bold text-slate-100 group-hover:text-blue-300">
            {p.name}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <CompliancePill flag={p.compliance_flag} />
          <span
            data-testid={pillTestId}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${pillCls}`}
          >
            {pillIcon}
            {pillText}
          </span>
        </div>
      </div>
      {firstIssueLine && (
        <div
          data-testid={`flagship-card-issue-line-${p.slug}`}
          className="mb-3 rounded-md border border-rose-500/30 bg-rose-950/30 px-2 py-1 text-[11px] text-rose-200/90 line-clamp-2"
        >
          {firstIssueLine}
        </div>
      )}
      {p.clinic_facing_description && (
        <p className="text-sm text-slate-400 leading-relaxed line-clamp-3">
          {p.clinic_facing_description}
        </p>
      )}
      <div className="mt-4 flex items-center justify-between text-xs">
        <div className="flex items-center gap-3 text-slate-500">
          <span className="inline-flex items-center gap-1">
            <Layers className="w-3 h-3" /> {p.tiers?.length || 0} tier{(p.tiers?.length || 0) === 1 ? "" : "s"}
          </span>
          <span className="inline-flex items-center gap-1">
            <FlaskConical className="w-3 h-3" /> {p.services?.length || 0} services
          </span>
        </div>
        <div className="text-slate-300 font-semibold">
          {lowestTier !== null ? `from $${(lowestTier / 100).toFixed(0)}` : "—"}
          <ArrowRight className="inline-block ml-1 w-3 h-3 text-slate-500 group-hover:text-blue-400 transition" />
        </div>
      </div>
    </Link>
  );
}

export default function AdminPrograms() {
  const [programs, setPrograms] = useState([]);
  const [readinessById, setReadinessById] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/flagship-programs");
      const list = r.data?.programs || [];
      setPrograms(list);
      // Fetch publish-readiness for each program in parallel. Best-effort;
      // card falls back to Active/Draft pill on failure.
      const results = await Promise.all(
        list.map((p) =>
          api
            .get(`/admin/flagship-programs/${p.id}/publish-readiness`)
            .then((rr) => [p.id, rr.data])
            .catch(() => [p.id, null])
        )
      );
      setReadinessById(Object.fromEntries(results.filter(([, v]) => v)));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createBlank = async () => {
    const slug = window.prompt(
      "Slug for the new program (lowercase, underscores, e.g. metabolic_health_weight_optimization):"
    );
    if (!slug) return;
    const name = window.prompt("Program name (e.g. Metabolic Health & Weight Optimization):");
    if (!name) return;
    setCreating(true);
    try {
      await api.post("/admin/flagship-programs", {
        slug,
        name,
        description: "",
        compliance_flag: "counsel_required",
        services: [],
        tiers: [],
      });
      toast.success("Program created. Add services + tiers in the detail editor.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Admin · Flagship programs
            </div>
            <h1 className="mt-1 text-3xl font-bold">Program Configuration</h1>
            <p className="mt-2 text-sm text-slate-400 max-w-2xl">
              Build turnkey clinic programs with compliant payment routing per service. Each program supports
              tiered patient pricing, vendor cost tracking, editable Nodera fees, and a live margin preview.
            </p>
          </div>
          <button
            type="button"
            onClick={createBlank}
            disabled={creating}
            data-testid="flagship-create-program"
            className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
            {creating ? "Creating…" : "New program"}
          </button>
        </div>

        {loading ? (
          <div className="mt-10 text-sm text-slate-500">Loading flagship programs…</div>
        ) : programs.length === 0 ? (
          <div
            data-testid="flagship-empty-state"
            className="mt-10 rounded-2xl border border-dashed border-slate-800 bg-slate-900/30 p-12 text-center"
          >
            <AlertCircle className="mx-auto h-8 w-8 text-amber-400 mb-3" />
            <div className="text-base font-semibold text-slate-200">No flagship programs yet</div>
            <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">
              Seed the Sleep & Recovery Optimization template with{" "}
              <code className="text-amber-300">python seed_flagship_sleep.py</code> from <code>/app/backend/</code>,
              or click "New program" to build one from scratch.
            </p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {programs.map((p) => (
              <ProgramCard key={p.id} p={p} readiness={readinessById[p.id]} />
            ))}
          </div>
        )}

        {programs.length > 0 && (
          <div
            className="mt-10 rounded-2xl border border-emerald-500/20 bg-emerald-950/30 p-4 text-xs text-emerald-200/80"
            data-testid="flagship-iter-83-status"
          >
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-none" />
              <div>
                <div className="font-semibold text-emerald-200 mb-1">
                  Iter 83 — Foundation layer ✓
                </div>
                You can now configure program metadata, tiers, services, payment routing (6 route types),
                Nodera fees (flat or %), compliance gates, and see a live margin breakdown per tier. Next iteration
                (iter 84) wires Stripe Connect destination splits for the actual one-checkout patient flow.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
